import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { closeMiniApp, initDataUser } from '@telegram-apps/sdk-react';
import NoTicketsError from '@pages/printer/components/errorComponents/NoTicketsError.component.jsx';
import PromoterAccessError from '@pages/printer/components/errorComponents/PromoterAccessError.component.jsx';
import { usePrinterSummationPage } from '@pages/printerSummation/hooks/usePrinterSummationPage.hook';
import FormDialog from '@components/dialogs/FormDialog.component.jsx';
import PrinterDialog from '@components/dialogs/PrinterDialog.component.jsx';
import { Survey } from '@components/forms/Survey/index.js';
import GeneralError from '@components/GeneralError.component.jsx';
import CardsSection from '@components/sections/CardSection.component.jsx';
import GlassTicket from '@components/shared/GlassTicket';
import Heading from '@components/shared/Heading.component.jsx';
import SubHeading from '@components/shared/SubHeading.component.jsx';
import EventLayout from '@layouts/Event.layout.jsx';

export default function PrinterSummationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = initDataUser().id;
  const { printerId } = useParams();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);

  const {
    getTickets,
    tickets,
    ticketsCount,
    isSurveyDone,
    user,
    isLangLoading,
    isSurveyLoading,
    surveyResult,
    sendToPrinter,
    showPreloader,
    isError,
    totalSum,
  } = usePrinterSummationPage();

  const isButtonsEnabled = [
    !showPreloader,
    !isFormOpen,
    !isPrintModalOpen,
    tickets.length > 0,
  ].every(Boolean);

  const hideButtonsImmediately = [isFormOpen || isPrintModalOpen].some(Boolean);

  const mainButtonParams = {
    text: t('printer.mainButton'),
    isEnabled: isButtonsEnabled,
    isVisible: isButtonsEnabled,
    onClick: handleButtonClick,
  };
  const secondaryButtonParams = {
    text: t('common.rules'),
    isEnabled: isButtonsEnabled,
    isVisible: isButtonsEnabled,
  };

  const returnToMaiPage = () =>
    navigate('/', {
      replace: true,
      state: { isInternal: true },
    });

  function showForm() {
    setIsFormOpen(true);
  }

  function requestPrint() {
    setIsFormOpen(false);
    setIsPrintModalOpen(true);
    sendToPrinter(userId, printerId);
  }

  function handleButtonClick() {
    return isSurveyDone ? requestPrint() : showForm();
  }

  useEffect(() => {
    getTickets(userId);
  }, [getTickets, userId]);

  useEffect(() => {
    if (surveyResult && !isSurveyLoading && !isError) {
      setIsFormOpen(false);
      setIsPrintModalOpen(true);
    }
  }, [surveyResult, isError, isSurveyLoading]);

  if (isError && !isLangLoading) {
    return <GeneralError showPreloader={showPreloader} />;
  }

  if (user?.isPromoter) {
    return <PromoterAccessError showPreloader={showPreloader} />;
  }

  if (tickets?.length === 0) {
    return (
      <NoTicketsError
        showPreloader={showPreloader}
        onButtonClick={returnToMaiPage}
      />
    );
  }

  return (
    <EventLayout
      showPreloader={showPreloader}
      mainButtonParams={mainButtonParams}
      secondaryButtonParams={secondaryButtonParams}
      hideButtonsImmediately={hideButtonsImmediately}
      footerText={t('printer.footer')}
    >
      <FormDialog
        height={'h-[75vh]'}
        open={isFormOpen && !showPreloader}
        onClose={() => setIsFormOpen(false)}
        title={t('printer.form.title')}
      >
        <Survey afterSubmit={requestPrint} />
      </FormDialog>

      <PrinterDialog
        open={isPrintModalOpen && !showPreloader}
        onClose={() => setIsPrintModalOpen(false)}
        onButtonClick={returnToMaiPage}
        onSecondaryButtonClick={() => closeMiniApp()}
      />

      <Heading className={'mb-3'}>{t('printer.header.available')}</Heading>
      <SubHeading>
        {t('printerSummation.subHeader.available', {
          totalSum,
          count: ticketsCount,
        })}
      </SubHeading>
      <CardsSection>
        {tickets.map(({ check_sum, purchase_place }) => (
          <GlassTicket
            key={crypto.randomUUID()}
            price={check_sum}
            store={purchase_place}
          />
        ))}
      </CardsSection>
    </EventLayout>
  );
}
